import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Modal from '@/v2/components/common/Modal'
import Button from '@/v2/components/common/Button'
import CheckboxField from '@/v2/components/common/CheckboxField'
import Typography from '@/v2/components/common/Typography'
import PatientAvatar from '@/v2/components/common/PatientAvatar'
import { useAllowedClinics } from '@/v2/hooks/useAllowedClinics'
import { getHostname } from '@/v2/utils/hostname'
import { useSettingsCancellationNotice } from '@/v2/hooks/settings/useSettingsCancellationNotice'
import {
  REASONS,
  appointmentValues,
  renderNotice,
} from '@/v2/components/settings/cancellation/cancellationTemplate'
import { Bubble, Count } from '@/v2/components/settings/reschedule/editorParts'
import { smsCount } from '@/v2/components/settings/reschedule/plainTemplate'
import { safeFormat } from '@/v2/utils/convert'
import { formatPhone, isHealthHistoryDone } from '../utils'

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #eeeded;
  border-radius: 4px;

  b {
    display: block;
    font-weight: 500;
  }

  span {
    color: #7d7d7d;
    font-size: 13px;
  }
`

const ReasonOption = styled(FormControlLabel)`
  && {
    margin: 0;
    padding: 4px 12px 4px 4px;
    border: 1px solid ${({ $selected }) => ($selected ? '#076AFF' : '#eeeded')};
    border-radius: 4px;
    background: ${({ $selected }) => ($selected ? '#f2f7ff' : '#fff')};
  }

  .MuiFormControlLabel-label {
    font-size: 14px;
  }
`

const SmsPreview = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px 16px;
  border-radius: 4px;
  background: #f9f9f9;
`

const EmailPreview = styled.div`
  border: 1px solid #eeeded;
  border-radius: 4px;
  overflow: hidden;
  font-size: 14px;

  div:first-child {
    padding: 10px 14px;
    background: #f9f9f9;
    font-size: 13px;
  }

  div:last-child {
    padding: 16px 14px;
    line-height: 1.55;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`

const Off = styled.div`
  padding: 12px 14px;
  border-radius: 4px;
  background: #f9f9f9;
  color: #7d7d7d;
  font-size: 13px;
`

// A template edited as code can hold Liquid logic only the server renders.
const NO_PREVIEW =
  'This message has logic of its own, so it can only be previewed once sent.'

const channelsOf = notice =>
  [notice?.bySms && 'text', notice?.byEmail && 'email'].filter(Boolean)

const CancelAppointmentModal = ({
  appointment,
  loading,
  onClose,
  onCancel,
}) => {
  const navigate = useNavigate()
  const { notice } = useSettingsCancellationNotice()
  const { clinics } = useAllowedClinics()
  const [reason, setReason] = useState('both')
  const [sendNotice, setSendNotice] = useState(true)
  const [preview, setPreview] = useState('sms')

  useEffect(() => {
    if (!appointment) return
    setReason(isHealthHistoryDone(appointment) ? 'not_confirmed' : 'both')
    setSendNotice(true)
    setPreview('sms')
  }, [appointment])

  if (!appointment) return null

  const { patient, appointmentType, doctor, startsAt } = appointment
  const channels = channelsOf(notice)
  const willSend = sendNotice && channels.length > 0
  const values = appointmentValues({
    appointment,
    reason: notice?.reasons?.[REASONS.find(r => r.value === reason).key] || '',
    // The clinics endpoint carries no name; the clinic switcher reads it here.
    clinicName: clinics.find(c => c.subdomain === getHostname().subdomain)
      ?.name,
    bookingUrl: notice?.bookingPageUrl,
  })
  const sms = renderNotice(notice?.smsMessage, values)
  const subject = renderNotice(notice?.emailSubject, values)
  const email = renderNotice(notice?.emailMessage, values)

  return (
    <Modal
      isOpen
      title="Cancel appointment"
      maxWidth="sm"
      fullWidth
      loading={!notice}
      cancelLabel="Keep appointment"
      confirmLabel={willSend ? 'Cancel & send notice' : 'Cancel appointment'}
      submitDisabled={loading}
      onClose={onClose}
      onConfirm={() => onCancel({ reason, sendNotice: willSend })}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Summary>
          <PatientAvatar patient={patient} size="44px" />
          <div>
            <b>{patient.fullName}</b>
            <span>
              {[
                appointmentType?.title,
                `${safeFormat(startsAt, 'EEEE, MMM d')} at ${safeFormat(
                  startsAt,
                  'h:mm a'
                )}`,
                doctor && `Dr. ${doctor.fullName}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </Summary>

        <Box>
          <Typography bold fontSize="14px" sx={{ mb: 1 }}>
            Why is it being cancelled?
          </Typography>
          <RadioGroup
            value={reason}
            onChange={event => setReason(event.target.value)}
            sx={{ gap: 1 }}
          >
            {REASONS.map(option => (
              <ReasonOption
                key={option.value}
                value={option.value}
                label={option.label}
                $selected={reason === option.value}
                control={<Radio size="small" />}
              />
            ))}
          </RadioGroup>
        </Box>

        {channels.length ? (
          <CheckboxField
            label={`Send the cancellation notice to ${
              patient.firstName
            } by ${channels.join(' and ')}`}
            checked={sendNotice}
            onChange={event => setSendNotice(event.target.checked)}
          />
        ) : (
          <Off>
            The cancellation notice is turned off, so {patient.firstName} won't
            be told. Turn it on in Settings → Reminders.
          </Off>
        )}

        {willSend && (
          <Box>
            <Tabs
              value={
                notice.bySms && notice.byEmail
                  ? preview
                  : notice.bySms
                  ? 'sms'
                  : 'email'
              }
              onChange={(event, value) => setPreview(value)}
              sx={{ mb: 1.5 }}
            >
              {notice.bySms && <Tab value="sms" label="Text" />}
              {notice.byEmail && <Tab value="email" label="Email" />}
            </Tabs>
            {notice.bySms && (preview === 'sms' || !notice.byEmail) ? (
              <SmsPreview>
                <Bubble>{sms ?? NO_PREVIEW}</Bubble>
                {sms && (
                  <Count>
                    To {formatPhone(patient.phoneNumber)} · {sms.length}{' '}
                    characters · {smsCount(sms)} SMS
                  </Count>
                )}
              </SmsPreview>
            ) : (
              <EmailPreview>
                <div>
                  Subject: <b>{subject}</b>
                </div>
                <div>{email ?? NO_PREVIEW}</div>
              </EmailPreview>
            )}
          </Box>
        )}

        {!willSend && channels.length > 0 && (
          <Off>
            The appointment is cancelled without telling {patient.firstName}.
          </Off>
        )}

        <Button
          variant="text"
          color="link"
          sx={{ alignSelf: 'flex-start', p: 0 }}
          onClick={() => navigate('/v2/settings/reminders')}
        >
          Edit the notice in Settings
        </Button>
      </Box>
    </Modal>
  )
}

export default CancelAppointmentModal
